import type { ExtraFilterItem, ExtraFilterOption } from "~/features/filter/filter.form";
import { Namespace } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { getEnumName } from "@racoon/lib";
import { AI_VERDICTS } from "./ai-verification.verdicts";

export type RecordTypeRoute =
  | "service-providers"
  | "waste-export"
  | "pickup-locations"
  | "trucks"
  | "depots"
  | "disposal-sites"
  | "suspicious-sites"
  | "anomalies";

/** Props shape expected by `FilterForm` for the records list (extra rows only; no waste-type tabs). */
export type RecordsListFilterFormConfig = {
  extraFilters: ExtraFilterItem[];
};

/**
 * Runtime options from gov geo access (districts), service providers (MVO) and
 * container types.
 */
export type RecordsListFilterDynamicOptions = {
  districtOptions: ExtraFilterOption[];
  mvoOptions: ExtraFilterOption[];
  containerTypeOptions: ExtraFilterOption[];
  /** Trucks registry: options derived from the loaded trucks list (see gov-trucks-filter). */
  equipmentTypeOptions?: ExtraFilterOption[];
  brandOptions?: ExtraFilterOption[];
  yearOptions?: ExtraFilterOption[];
  garageOptions?: ExtraFilterOption[];
};

type TFn = (namespace: Namespace, key: any) => string;

function tG(t: TFn, key: string) {
  return t(Namespace.Govdash, key as any);
}

// Meaningful job statuses for the waste-export filter (UNDEFINED omitted). Each
// option's value is the numeric JobStatus enum member (stringified for the filter
// form); its label reuses the shared JobStatus translations from the `enum`
// namespace, resolved via getEnumName so we don't duplicate them here.
const JOB_STATUS_FILTER_VALUES: ConnectProto.JobStatus[] = [
  ConnectProto.JobStatus.CREATED,
  ConnectProto.JobStatus.ASSIGNED,
  ConnectProto.JobStatus.ENROUTE,
  ConnectProto.JobStatus.ON_SERVICE,
  ConnectProto.JobStatus.COMPLETED,
  ConnectProto.JobStatus.CANCELLED,
  ConnectProto.JobStatus.NOT_COMPLETED,
];

function jobStatusRow(t: TFn): ExtraFilterItem {
  return {
    key: "jobStatus",
    label: tG(t, "wasteExport.exportStatus"),
    options: JOB_STATUS_FILTER_VALUES.map((status) => {
      const enumKey = getEnumName(ConnectProto.JobStatusSchema, status);
      return {
        value: String(status),
        label: enumKey ? t(Namespace.Enum, enumKey) : String(status),
      };
    }),
    selectedOptions: [],
  };
}

/**
 * The photo-recognition verdicts. Each option is one bad verdict, sent as its
 * `badRange` in `score_filters` (see ai-verification.verdicts), so the list filters
 * on exactly what the card pills flag. Single-select because the API ANDs score
 * filters: several picks would list only jobs with all of those problems, not any.
 */
function aiVerdictRow(t: TFn): ExtraFilterItem {
  return {
    key: "aiVerdict",
    label: tG(t, "filters.aiVerdict.label"),
    options: AI_VERDICTS.map(({ field, badKey }) => ({ value: field, label: tG(t, badKey) })),
    selectedOptions: [],
    singleSelect: true,
  };
}

function districtRow(t: TFn, options: ExtraFilterOption[]): ExtraFilterItem {
  return {
    key: "district",
    label: tG(t, "filters.districts.label"),
    options,
    selectedOptions: [],
  };
}

function mvoRow(t: TFn, options: ExtraFilterOption[]): ExtraFilterItem {
  return {
    key: "mvo",
    label: tG(t, "filters.mvo.label"),
    options,
    selectedOptions: [],
  };
}

function containerTypeRow(t: TFn, options: ExtraFilterOption[]): ExtraFilterItem {
  return {
    key: "containerType",
    label: tG(t, "filters.containerType.label"),
    options,
    selectedOptions: [],
  };
}

// ── Trucks registry rows ──
// The trucks registry groups by MVO already and the Trucks RPC returns only
// ACTIVE trucks, so there are no MVO or status rows. Options for the data-backed
// rows are derived from the loaded list by deriveGovTrucksFilterOptions (which
// also documents, with prod numbers, why eco class / engine type were dropped
// and which backend release the garage/shift rows depend on); filtering itself
// happens in applyGovTrucksExtraFilters.

function equipmentTypeRow(t: TFn, options: ExtraFilterOption[]): ExtraFilterItem {
  return {
    key: "equipmentType",
    label: tG(t, "filters.equipment.label"),
    options,
    selectedOptions: [],
  };
}

function brandRow(t: TFn, options: ExtraFilterOption[]): ExtraFilterItem {
  return {
    key: "brand",
    label: tG(t, "filters.brand.label"),
    options,
    selectedOptions: [],
  };
}

function yearRow(t: TFn, options: ExtraFilterOption[]): ExtraFilterItem {
  return {
    key: "year",
    label: tG(t, "filters.year.label"),
    options,
    selectedOptions: [],
  };
}

function garageRow(t: TFn, options: ExtraFilterOption[]): ExtraFilterItem {
  return {
    key: "garage",
    label: tG(t, "filters.garage.label"),
    options,
    selectedOptions: [],
  };
}

/** On line right now / shift ended / never went on shift. */
function shiftStateRow(t: TFn): ExtraFilterItem {
  return {
    key: "shiftState",
    label: tG(t, "filters.shiftState.label"),
    options: [
      { value: "onShift", label: tG(t, "filters.shiftState.options.onShift") },
      { value: "offShift", label: tG(t, "filters.shiftState.options.offShift") },
      { value: "never", label: tG(t, "filters.shiftState.options.never") },
    ],
    selectedOptions: [],
  };
}

/** When the truck's last shift started. Buckets match gov-trucks-filter's LastShiftBucket. */
function lastShiftRow(t: TFn): ExtraFilterItem {
  return {
    key: "lastShift",
    label: tG(t, "filters.lastShift.label"),
    options: [
      { value: "today", label: tG(t, "filters.lastShift.options.today") },
      { value: "week", label: tG(t, "filters.lastShift.options.week") },
      { value: "month", label: tG(t, "filters.lastShift.options.month") },
      { value: "older", label: tG(t, "filters.lastShift.options.older") },
    ],
    selectedOptions: [],
  };
}

/**
 * Archive state for the suspicious-sites registry. Nothing selected is the
 * default view - active locations only; "all" and "archived" are explicit
 * opt-ins. Applied client-side in records.page (the RPC returns the whole
 * registry in one response).
 */
function archiveStateRow(t: TFn): ExtraFilterItem {
  return {
    key: "archiveState",
    label: tG(t, "filters.archiveState.label"),
    options: [
      { value: "all", label: tG(t, "filters.archiveState.options.all") },
      { value: "active", label: tG(t, "filters.archiveState.options.active") },
      { value: "archived", label: tG(t, "filters.archiveState.options.archived") },
    ],
    selectedOptions: [],
  };
}

/**
 * Filter UI config for the records list (list view only).
 * Returns `null` when this registry type has no filters (button hidden).
 */
export function getRecordsListFilterConfig(
  t: TFn,
  recordType: string | undefined,
  dynamic?: RecordsListFilterDynamicOptions,
  /** Which waste-export tab the list is on; the AI list supports fewer filters. */
  options?: { wasteExportTab?: "jobs" | "ai" },
): RecordsListFilterFormConfig | null {
  if (!recordType) return null;

  const districtOptions = dynamic?.districtOptions ?? [];
  const mvoOptions = dynamic?.mvoOptions ?? [];
  const containerTypeOptions = dynamic?.containerTypeOptions ?? [];

  switch (recordType as RecordTypeRoute) {
    case "anomalies":
    case "disposal-sites":
      return null;

    case "service-providers":
      return {
        extraFilters: [districtRow(t, districtOptions)],
      };

    case "suspicious-sites":
      return {
        extraFilters: [archiveStateRow(t)],
      };

    case "pickup-locations":
      return {
        extraFilters: [
          districtRow(t, districtOptions),
          mvoRow(t, mvoOptions),
          containerTypeRow(t, containerTypeOptions),
        ],
      };

    case "trucks":
      return {
        extraFilters: [
          garageRow(t, dynamic?.garageOptions ?? []),
          equipmentTypeRow(t, dynamic?.equipmentTypeOptions ?? []),
          brandRow(t, dynamic?.brandOptions ?? []),
          yearRow(t, dynamic?.yearOptions ?? []),
          shiftStateRow(t),
          lastShiftRow(t),
        ],
      };

    case "depots":
      return {
        extraFilters: [mvoRow(t, mvoOptions)],
      };

    case "waste-export":
      // AiVerificationListRequest has no job-status filter (it filters on verdicts
      // instead), so offering one on the AI tab would be a control that does nothing.
      return {
        extraFilters:
          options?.wasteExportTab === "ai"
            ? [aiVerdictRow(t), districtRow(t, districtOptions), mvoRow(t, mvoOptions)]
            : [jobStatusRow(t), districtRow(t, districtOptions), mvoRow(t, mvoOptions)],
      };

    default:
      return null;
  }
}

export function hasRecordsListFilters(recordType: string | undefined): boolean {
  if (!recordType) return false;
  switch (recordType as RecordTypeRoute) {
    case "anomalies":
    case "disposal-sites":
      return false;
    case "service-providers":
    case "suspicious-sites":
    case "pickup-locations":
    case "trucks":
    case "depots":
    case "waste-export":
      return true;
    default:
      return false;
  }
}
